import { createFileRoute } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roof-energy-efficiency-r-value";
const TITLE = "Foam Roof R-Value & Phoenix Energy Savings Guide | Durafoam";
const DESCRIPTION =
  "Spray foam roofs deliver R-6.5 to R-7 per inch — the highest R-value of any roofing system. See how a Phoenix SPF roof cuts cooling bills 15–35% and why it pays back faster here than anywhere else in the country.";

const faqs = [
  {
    q: "What is the R-value of a spray foam roof?",
    a: "Closed-cell spray polyurethane foam (SPF) provides R-6.5 to R-7 per inch — the highest R-value per inch of any commercially available roofing insulation. A standard 1.5-inch Durafoam roof installs at roughly R-10 to R-10.5, and most Phoenix re-roofs are sprayed at 1.5 to 2 inches for that reason.",
  },
  {
    q: "How much can a foam roof lower my Phoenix cooling bill?",
    a: "Independent SPF case studies, including documented results from manufacturers like West Roofing Systems, show 15–35% reductions in cooling costs after a foam roof retrofit. The exact number depends on your prior roof (uninsulated flat roof vs. tile with attic insulation), HVAC efficiency, and how many south- and west-facing slopes you have.",
  },
  {
    q: "Why does R-value matter more in Phoenix than other cities?",
    a: "Phoenix runs 100+ days a year above 100°F and roof surface temperatures regularly hit 160–180°F. Every R-point keeps more of that heat out of your conditioned space — which is why SPF payback periods in the Valley are typically 3–7 years vs. 7–15 years in cooler climates.",
  },
  {
    q: "Does the white reflective coating add to energy savings?",
    a: "Yes. The elastomeric or silicone topcoat reflects 80–85% of solar radiation, so the foam underneath never gets as hot in the first place. That's why a properly recoated foam roof outperforms its raw R-value on paper — you're stacking reflection on top of insulation.",
  },
  {
    q: "Does R-value drop as the foam ages?",
    a: "Closed-cell SPF holds its R-value extremely well — long-term thermal resistance (LTTR) testing shows minimal drop-off over 20+ years when the roof is recoated on schedule. The biggest threat to R-value is letting the coating fail, exposing the foam to UV and moisture.",
  },
];

export const Route = createFileRoute("/foam-roof-energy-efficiency-r-value")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Foam Roof Energy Efficiency Guide",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="foam-roofing"
      eyebrow="Phoenix energy guide"
      h1="Spray Foam Roof R-Value & Phoenix Energy Savings"
      subhead="Closed-cell spray polyurethane foam delivers R-6.5 to R-7 per inch — the highest of any roofing system. Here's what that means for a Phoenix cooling bill."
      heroBullets={[
        "R-6.5 to R-7 per inch (closed-cell SPF)",
        "Typical Phoenix cooling savings: 15–35%",
        "Payback in the Valley: usually 3–7 years",
        "Reflective topcoat blocks 80–85% of solar heat",
      ]}
      whatIs={{
        heading: "What R-value actually means on a Phoenix roof",
        body: (
          <>
            <p>
              <strong>R-value</strong> measures how well a material resists heat flow — higher
              is better. Closed-cell spray polyurethane foam (SPF) is rated at <strong>R-6.5 to
              R-7 per inch</strong>, which is the highest R-value per inch of any roofing
              insulation on the market. By comparison, polyiso board sits around R-5.7 per inch
              (and drops in heat), EPS around R-4, and a bare tile roof contributes essentially
              zero — all the insulation is in your attic.
            </p>
            <p>
              A typical Durafoam install is <strong>1.5 to 2 inches thick</strong>, putting your
              roof at <strong>R-10 to R-14</strong> from the foam alone. Stack that on top of
              whatever insulation already exists in the attic or ceiling cavity and you've
              effectively rebuilt your home's thermal envelope at the most important surface —
              the one the sun hits all day.
            </p>
            <p>
              R-value isn't just an insulation number — it's the difference between an AC unit
              cycling all afternoon and one that cools the house and shuts off.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Why Phoenix gets the best energy payback in the country",
        body: (
          <>
            <p>
              Phoenix roof surface temperatures regularly hit <strong>160–180°F</strong> during
              summer afternoons. Every BTU of that heat that crosses your roof deck has to be
              removed by your HVAC system — which is exactly what drives a $400+ summer
              electric bill.
            </p>
            <p>
              Independent case studies on SPF roof retrofits — including the energy-performance
              data published by manufacturers like West Roofing Systems — consistently show{" "}
              <strong>15% to 35% reductions in cooling costs</strong> after a spray foam roof
              is installed. The variation comes down to:
            </p>
            <ul>
              <li><strong>What you had before.</strong> Replacing an uninsulated built-up flat roof? Expect savings at the high end. Replacing a well-insulated tile roof? More modest, but still real.</li>
              <li><strong>HVAC efficiency.</strong> A 20-year-old 8-SEER unit will show bigger gains than a brand-new heat pump that was already barely keeping up.</li>
              <li><strong>Exposure.</strong> South- and west-facing slopes take the worst beating. The more of your roof faces them, the more SPF saves you.</li>
              <li><strong>Coating reflectivity.</strong> A fresh white silicone or elastomeric topcoat reflects <strong>80–85% of incoming solar radiation</strong>. That reflection is in addition to the foam's R-value — you're stacking two energy-saving mechanisms.</li>
            </ul>
            <p>
              Payback in the Valley is typically <strong>3 to 7 years</strong> on energy
              savings alone — well ahead of cooler markets where the same install takes 7–15
              years to pencil out. That's before you count the leak elimination, the seamless
              monolithic membrane, and a foam roof's 25–40+ year service life with on-time
              recoats.
            </p>
          </>
        ),
      }}
      process={{
        heading: "How a Durafoam install delivers the R-value on paper",
        intro:
          "R-value is only as good as the install. Cheap SPF jobs hit the number in a brochure but lose it in real life through thin spots, voids, or skipped coating mil thickness. Our process protects the rated R-value across the whole roof.",
        steps: [
          { title: "Measure & assess", body: "We measure the roof, document existing insulation, and identify hot spots (HVAC penetrations, west-facing slopes) that drive your bill." },
          { title: "Prep the deck", body: "Old roofing is removed or scarified, decking is dried, and the surface is primed so the SPF bonds correctly — a poor bond becomes a void, and a void becomes an R-value gap." },
          { title: "Spray to spec", body: "Closed-cell SPF is applied in passes to a documented thickness — typically 1.5–2 inches for Phoenix — and we verify pass thickness across the roof, not just at a corner sample." },
          { title: "Coat for reflectivity", body: "A primer plus two coats of silicone or elastomeric topcoat at manufacturer-required wet mil thickness. We document mil readings; cheaper crews skip this and you lose the reflective performance." },
          { title: "Recoat on schedule", body: "Every 5 years in Phoenix. On-time recoats preserve both the foam's R-value and the topcoat's reflectivity for 25–40+ years of energy savings." },
        ],
      }}
      whyChoose={{
        heading: "How SPF stacks up against other roofing insulation",
        body: (
          <>
            <ul>
              <li><strong>Closed-cell SPF:</strong> R-6.5 to R-7 per inch. Seamless, self-flashing, adds reflectivity with topcoat. Long-term R-value retention is excellent.</li>
              <li><strong>Polyiso board:</strong> R-5.7 per inch on paper but drops in cold and degrades over time. Has seams that leak and that need flashing.</li>
              <li><strong>EPS / XPS board:</strong> R-4 to R-5 per inch. Cheaper, but seams and fasteners create thermal bridges that drop real-world R-value.</li>
              <li><strong>Bare tile or shingle:</strong> Effectively R-0 from the roof itself — you're relying entirely on attic insulation, which Phoenix code only requires at R-38.</li>
            </ul>
            <p>
              The takeaway: foam is the only roof system where the roof <em>is</em> the
              insulation. Everything else is "roof on top, insulation elsewhere" — and every
              joint between those layers is a thermal weak point.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Find out what a foam roof would save on your Phoenix home"
      ctaBody="We'll measure your roof, look at your current insulation and exposures, and give you a real estimate of cooling savings — not a marketing range. Free, no pressure."
    />
  );
}
